import { toast } from 'sonner';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CallLogDialog from './CallLogDialog';

const ago = (iso) => {
  const d = (Date.now() - new Date(iso).getTime()) / 86400000;
  if (d < 1 / 24) return 'just now';
  if (d < 1) return `${Math.floor(d * 24)}h ago`;
  return d < 2 ? 'yesterday' : `${Math.floor(d)} days ago`;
};
import { Mail, Phone, Globe, Linkedin, Copy, Briefcase, Building2, MapPin, ExternalLink } from 'lucide-react';

// A rich, sales-ready contact/company row: name, role, company, email, phone, website,
// location and source - with click-to-email, click-to-call, open-website/LinkedIn and
// copy-email actions. Works on dark (slate) and light tones.
export default function ContactRow({ m, tone = 'light', objectType = 'contact' }) {
  const navigate = useNavigate();
  const [callOpen, setCallOpen] = useState(false);
  const [lastCall, setLastCall] = useState(m.last_call || null);
  const dark = tone === 'dark';
  const isCompany = objectType === 'company' || !!m.record_type;
  const openRecord = () => navigate(isCompany ? `/admin/companies?open=${m.id}` : `/admin/contacts?open=${m.id}`);
  const companyId = isCompany ? m.id : m.company_id;
  const name = m.full_name || m.name || m.company_name || 'Unnamed';
  const role = m.job_title || m.role || m.sub_segment || '';
  const company = m.company_name || (m.record_type ? m.name : '') || '';
  const email = m.email || m.contact_email || '';
  const phone = m.phone || (m.listing && m.listing.phone) || m.company_phone || '';
  const phoneIsOffice = !m.phone && !!m.company_phone;
  const website = m.website || (m.domain ? `https://${m.domain}` : '');
  const linkedin = m.linkedin_url || '';
  const location = m.city || m.region || m.location || m.county || (m.listing && m.listing.region) || '';
  const source = m.source || m.lifecycle_stage || m.public_status || m.status || '';

  const copy = (val) => { navigator.clipboard?.writeText(val).then(() => toast.success('Email copied')).catch(() => {}); };

  const c = dark
    ? { card: 'bg-slate-950 border-slate-800', name: 'text-white', sub: 'text-slate-400', meta: 'text-slate-500',
        chip: 'bg-slate-900 border-slate-700 text-slate-200 hover:border-[#00A79D] hover:text-white', tag: 'bg-slate-800 text-slate-300' }
    : { card: 'bg-white border-gray-200', name: 'text-gray-900', sub: 'text-gray-500', meta: 'text-gray-400',
        chip: 'bg-gray-50 border-gray-200 text-gray-600 hover:border-[#00A79D] hover:text-[#00857D]', tag: 'bg-gray-100 text-gray-600' };

  const Chip = ({ href, onClick, icon: Icon, label, testid }) => {
    const cls = `inline-flex items-center gap-1.5 text-xs font-medium border rounded-md px-2.5 py-1 transition-colors ${c.chip}`;
    const inner = <><Icon className="w-3.5 h-3.5" /> {label}</>;
    return href
      ? <a href={href} onClick={onClick} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className={cls} data-testid={testid}>{inner}</a>
      : <button type="button" onClick={onClick} className={cls} data-testid={testid}>{inner}</button>;
  };

  return (
    <div className={`border rounded-lg p-3 ${c.card}`} data-testid={`contact-row-${m.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <button type="button" onClick={openRecord} title={`Open ${isCompany ? 'company' : 'contact'} card`}
            className={`text-sm font-semibold ${c.name} hover:text-[#00A79D] inline-flex items-center gap-1 text-left transition-colors`}
            data-testid={`contact-open-${m.id}`}>
            {name} <ExternalLink className="w-3 h-3 opacity-50" />
          </button>
          <p className={`text-xs ${c.sub} flex items-center gap-2 flex-wrap mt-0.5`}>
            {role && <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" />{role}</span>}
            {company && (companyId && !isCompany ? (
              <button type="button" onClick={() => navigate(`/admin/companies?open=${companyId}`)} title="Open company card"
                className="inline-flex items-center gap-1 hover:text-[#00A79D] underline-offset-2 hover:underline transition-colors"
                data-testid={`contact-company-open-${m.id}`}><Building2 className="w-3 h-3" />{company}</button>
            ) : <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" />{company}</span>)}
            {!company && !isCompany && <span className={`inline-flex items-center gap-1 ${c.meta}`}><Building2 className="w-3 h-3" />No company</span>}
            {location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{location}</span>}
          </p>
        </div>
        {source && <span className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded ${c.tag}`}>{source}</span>}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mt-2.5">
        {email ? <Chip href={`mailto:${email}`} icon={Mail} label={email} testid={`contact-email-${m.id}`} /> : <span className={`text-xs ${c.meta}`}>No email</span>}
        {email && <Chip onClick={() => copy(email)} icon={Copy} label="Copy" testid={`contact-copy-${m.id}`} />}
        {phone ? <Chip href={`tel:${phone}`} onClick={() => setCallOpen(true)} icon={Phone} label={phoneIsOffice ? `${phone} (office)` : phone} testid={`contact-phone-${m.id}`} />
          : <span className={`text-xs ${c.meta} inline-flex items-center gap-1`} data-testid={`contact-no-phone-${m.id}`}><Phone className="w-3 h-3" /> No phone</span>}
        {website && <Chip href={website} icon={Globe} label="Website" testid={`contact-website-${m.id}`} />}
        {linkedin && <Chip href={linkedin} icon={Linkedin} label="LinkedIn" testid={`contact-linkedin-${m.id}`} />}
      </div>
      {lastCall && (
        <p className={`text-[11px] mt-2 ${c.sub}`} data-testid={`contact-last-call-${m.id}`}>
          Last call: <span className="font-semibold">{lastCall.label}</span> · {ago(lastCall.at)}{lastCall.by ? ` · ${lastCall.by}` : ''}
        </p>
      )}
      <CallLogDialog open={callOpen} onOpenChange={setCallOpen} record={m} phone={phone}
        parentType={isCompany ? 'company' : 'contact'} onLogged={setLastCall} />
    </div>
  );
}
